// ─────────────────────────────────────────────
//  Seed Staff Short Codes
//  Admin action: writes shortCode onto matched `staff` doctor docs — the staff
//  equivalent of seedProviderCodes (which targets the `users` collection).
//
//  The board reads the `staff` collection (useStaff → listenToStaff), and the
//  Google-Sheet / PDF importers match attendings by shortCode. seedStaff seeds
//  people but no codes (seedDoctors carries none), so without this the staff
//  doctors have shortCode:null and every imported attending fails to resolve
//  ("unknown MD code"). This applies the same canonical OFFICIAL_DOCTOR_CODES
//  the users collection got, matched by name with no guessing.
//
//  Idempotent: a staff doctor already set to the correct code is skipped.
// ─────────────────────────────────────────────
import { auth } from "./config";
import { getDocs } from "./sdk";
import { staffCol, updateStaff } from "./firestore";
import { OFFICIAL_DOCTOR_CODES, matchCodesToUsers } from "../domain/providerCodes";

/**
 * Apply the official short-code roster to `staff` doctor docs.
 * Runs as the admin who clicked the button.
 *
 * @returns {Promise<{applied:number, skipped:number, unmatched:number, ambiguous:number}>}
 */
export async function seedStaffCodes() {
  if (!auth.currentUser) {
    throw new Error("Not authenticated — must be logged in as an admin.");
  }

  // The staff pool is shared facility-wide (listenToStaff has no facility
  // filter), so seed across the whole collection. Only doctor-role docs can
  // carry an attending short code.
  const snap = await getDocs(staffCol());
  const doctors = snap.docs
    .map((d) => ({ uid: d.id, ...d.data() }))
    .filter((s) => s.role === "doctor");

  // matchCodesToUsers matches OFFICIAL_DOCTOR_CODES ("Last, First") to records
  // by name tokens; it expects { uid, name } and never guesses. NOTE it also
  // internally keeps only role "anesthesiologist" (or no role), but the staff
  // doctor role string is "doctor" — so present them with the board role it
  // expects, else every doctor is filtered out and nothing matches.
  const candidates = doctors.map((d) => ({ ...d, role: "anesthesiologist" }));
  const { matches, unmatched, ambiguous } = matchCodesToUsers(OFFICIAL_DOCTOR_CODES, candidates);

  let applied = 0;
  let skipped = 0;
  for (const { uid, code } of matches) {
    const existing = doctors.find((s) => s.uid === uid);
    if (existing?.shortCode === code) { skipped += 1; continue; }
    await updateStaff(uid, { shortCode: code });
    applied += 1;
  }

  if (unmatched.length > 0) {
    console.log(
      "[seedStaffCodes] Unmatched codes (no staff doctor found — assign manually):",
      unmatched.map((u) => `${u.code}: ${u.name}`)
    );
  }
  if (ambiguous.length > 0) {
    console.log(
      "[seedStaffCodes] Ambiguous matches (2+ staff share name tokens — assign manually):",
      ambiguous.map((a) => `${a.code}: ${a.name} → ${a.candidateUids.join(", ")}`)
    );
  }

  return { applied, skipped, unmatched: unmatched.length, ambiguous: ambiguous.length };
}
